<template>
  <div class="w-full px-4">
    <div class="relative max-w-4xl mx-auto bg-white dark:bg-gray-700 rounded-2xl shadow-lg overflow-hidden">
      <!-- Title -->
      <div class="text-center py-6 px-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600">
        <h3 class="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white">
          {{ t('charts.comparison.title') }}
        </h3>
      </div>
      
      <!-- Desktop: Side by side -->
      <div class="hidden md:grid grid-cols-2 relative">
        <!-- Before -->
        <div class="p-6 border-r border-gray-200 dark:border-gray-600">
          <div class="bg-red-100 dark:bg-red-900/30 rounded-xl p-4 mb-4">
            <h4 class="text-lg font-bold text-red-600 dark:text-red-400 flex items-center gap-2">
              <font-awesome-icon icon="fa-solid fa-circle-xmark" />
              {{ t('charts.comparison.before_title') }}
            </h4>
          </div>
          
          <ul class="space-y-4">
            <li v-for="(item, index) in beforeItems" :key="index" class="flex items-start gap-3">
              <span class="text-red-500 mt-0.5">•</span>
              <span 
                class="text-gray-600 dark:text-gray-300"
                :class="{ 'font-bold text-red-600 dark:text-red-400': index === beforeItems.length - 1 }"
              >
                {{ item }}
              </span>
            </li>
          </ul>
        </div>
        
        <!-- After -->
        <div class="p-6">
          <div class="bg-green-100 dark:bg-green-900/30 rounded-xl p-4 mb-4">
            <h4 class="text-lg font-bold text-green-600 dark:text-green-400 flex items-center gap-2">
              <font-awesome-icon icon="fa-solid fa-circle-check" />
              {{ t('charts.comparison.after_title') }}
            </h4>
          </div>
          
          <ul class="space-y-4">
            <li v-for="(item, index) in afterItems" :key="index" class="flex items-start gap-3">
              <span class="text-green-500 mt-0.5">•</span>
              <span 
                class="text-gray-600 dark:text-gray-300"
                :class="{ 'font-bold text-green-600 dark:text-green-400': index === afterItems.length - 1 }"
              >
                {{ item }}
              </span>
            </li>
          </ul>
        </div>
        
        <!-- VS badge centered on divider -->
        <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
          <div class="w-12 h-12 rounded-full bg-gray-800 dark:bg-gray-600 flex items-center justify-center shadow-lg border-4 border-white dark:border-gray-700">
            <span class="text-white text-sm font-bold">VS</span>
          </div>
        </div>
      </div>
      
      <!-- Mobile: Stacked -->
      <div class="md:hidden">
        <!-- Before -->
        <div class="p-4 border-b border-gray-200 dark:border-gray-600">
          <div class="bg-red-100 dark:bg-red-900/30 rounded-lg p-3 mb-3">
            <h4 class="text-base font-bold text-red-600 dark:text-red-400 flex items-center gap-2">
              <font-awesome-icon icon="fa-solid fa-circle-xmark" />
              {{ t('charts.comparison.before_title') }}
            </h4>
          </div>
          
          <ul class="space-y-2">
            <li v-for="(item, index) in beforeItems" :key="'mobile-before-' + index" class="flex items-start gap-2 text-sm">
              <span class="text-red-500">•</span>
              <span 
                class="text-gray-600 dark:text-gray-300"
                :class="{ 'font-bold text-red-600 dark:text-red-400': index === beforeItems.length - 1 }"
              >
                {{ item }}
              </span>
            </li>
          </ul>
        </div>
        
        <!-- VS divider -->
        <div class="flex items-center justify-center py-2 bg-gray-100 dark:bg-gray-800">
          <div class="w-10 h-10 rounded-full bg-gray-800 dark:bg-gray-200 flex items-center justify-center">
            <span class="text-white dark:text-gray-800 text-sm font-bold">VS</span>
          </div>
        </div>
        
        <!-- After -->
        <div class="p-4">
          <div class="bg-green-100 dark:bg-green-900/30 rounded-lg p-3 mb-3">
            <h4 class="text-base font-bold text-green-600 dark:text-green-400 flex items-center gap-2">
              <font-awesome-icon icon="fa-solid fa-circle-check" />
              {{ t('charts.comparison.after_title') }}
            </h4>
          </div>
          
          <ul class="space-y-2">
            <li v-for="(item, index) in afterItems" :key="'mobile-after-' + index" class="flex items-start gap-2 text-sm">
              <span class="text-green-500">•</span>
              <span 
                class="text-gray-600 dark:text-gray-300"
                :class="{ 'font-bold text-green-600 dark:text-green-400': index === afterItems.length - 1 }"
              >
                {{ item }}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const beforeItems = computed(() => [
  t('charts.comparison.before_item1'),
  t('charts.comparison.before_item2'),
  t('charts.comparison.before_item3'),
  t('charts.comparison.before_time')
]);

const afterItems = computed(() => [
  t('charts.comparison.after_item1'),
  t('charts.comparison.after_item2'),
  t('charts.comparison.after_item3'),
  t('charts.comparison.after_time')
]);
</script>

<style scoped>
</style>
